import {
  Cancel01Icon,
  DashboardSpeed02Icon,
  Money04Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { ConfigureMetricsDrawerProps } from "../../types";
import {
  BaseInput,
  Button,
  ButtonVariant,
  Checkbox,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Select,
  Show,
  sizedSelectStyles
} from "shared/components";
import { useEffect, useState } from "react";
import { Tabs } from "shared/components/BaseTabs";
import { RoleSelector } from "./RoleSelector";
import { MetricWidget } from "../MetricWidget";
import { ApplicationMetricData, GroupStatMetricNames } from "api/applications";
import {
  CREDIT_METRICS,
  DEFAULT_CREDIT_METRIC,
  DEFAULT_INCOME_METRIC,
  GROUP_STAT_LABEL,
  INCOME_METRICS
} from "../../constants";
import { useConfigurableGroupMetric } from "../../hooks/use-configurable-group-metric";
import { Option } from "shared/components";

type MetricTab = "income" | "credit";

const baseSelectStyles = sizedSelectStyles();

const INCOME_METRIC_OPTIONS: Option[] = [
  {
    label: "Verified",
    options: [
      { value: GroupStatMetricNames.GrossIncome, label: "Gross income" },
      { value: GroupStatMetricNames.NetIncome, label: "Net income" },
      {
        value: GroupStatMetricNames.GrossIncomeToRentRatio,
        label: "Gross income to rent"
      },
      {
        value: GroupStatMetricNames.DebtToGrossIncome,
        label: "Debt to gross income"
      },
      {
        value: GroupStatMetricNames.DebtToNetIncome,
        label: "Debt to net income"
      }
    ]
  },
  {
    label: "Self-reported",
    options: [
      {
        value: GroupStatMetricNames.SelfReportedGrossIncome,
        label: "Gross income"
      },
      {
        value: GroupStatMetricNames.SelfReportedGrossIncomeToRent,
        label: "Gross income to rent"
      },
      {
        value: GroupStatMetricNames.SelfReportedDebtToGrossIncome,
        label: "Debt to gross income"
      }
    ]
  }
];

const CREDIT_METRIC_OPTIONS: Option[] = [
  {
    value: GroupStatMetricNames.AverageCreditScore,
    label: "Average credit score"
  },
  {
    value: GroupStatMetricNames.MaxCreditScore,
    label: "Highest credit score"
  },
  {
    value: GroupStatMetricNames.MinCreditScore,
    label: "Lowest credit score"
  },
  {
    value: GroupStatMetricNames.NoCreditScorePeople,
    label: "People with no credit score"
  }
];

export const ConfigureMetrics = ({
  onClose,
  existingMetric
}: ConfigureMetricsDrawerProps) => {
  const [activeTab, setActiveTab] = useState<MetricTab>("income");

  useEffect(() => {
    if (existingMetric && CREDIT_METRICS.includes(existingMetric.metric))
      setActiveTab("credit");
    else setActiveTab("income");
  }, [existingMetric]);

  const handleChangeActiveTab = (tab: MetricTab) => {
    const defaultMetric =
      tab === "income" ? DEFAULT_INCOME_METRIC : DEFAULT_CREDIT_METRIC;

    setCurrentMetric({ ...defaultMetric, index: existingMetric?.index });
    setActiveTab(tab);
  };

  const [currentMetric, setCurrentMetric] = useState<
    Partial<ApplicationMetricData>
  >(existingMetric || DEFAULT_INCOME_METRIC);

  const setCurrentMetricField = <K extends keyof ApplicationMetricData>(
    key: K,
    value: ApplicationMetricData[K]
  ) => setCurrentMetric((prev) => ({ ...prev, [key]: value }));

  const setMetricType = (metricType: GroupStatMetricNames) => {
    setCurrentMetric((prev) => ({
      ...prev,
      metric: metricType,
      title: GROUP_STAT_LABEL[metricType]
    }));
  };

  const { addNewMetric, updateMetric } = useConfigurableGroupMetric();
  const saveMetric = Boolean(existingMetric) ? updateMetric : addNewMetric;

  const availableOptions =
    activeTab === "income" ? INCOME_METRIC_OPTIONS : CREDIT_METRIC_OPTIONS;
  const currentMetricType = currentMetric.metric as GroupStatMetricNames;
  const allowSubmit = Boolean(currentMetric.roles?.length);

  return (
    <div className="h-full overflow-y-auto flex flex-col">
      <header className="p-6 text-xl font-bold flex justify-between items-center border-b border-alpha-100 bg-background-1000">
        Configure metrics
        <IconButton
          HugeIcon={Cancel01Icon}
          size={IconButtonSize.LG}
          variant={IconButtonVariant.TEXT_BASE}
          onClick={onClose}
        />
      </header>

      <div className="px-6 space-y-4 flex flex-col mb-6">
        <Tabs
          size="sm"
          active={activeTab}
          onChange={handleChangeActiveTab}
          tabStyle="outline"
          className="mt-4"
          tabs={[
            {
              value: "income",
              label: "Income",
              leftElement: {
                type: "icon",
                component: Money04Icon
              }
            },
            {
              value: "credit",
              label: "Credit",
              leftElement: {
                type: "icon",
                component: DashboardSpeed02Icon
              }
            }
          ]}
        />
        <Select
          id="metric-selector"
          label="Metric"
          value={currentMetric.metric}
          onChange={(e) =>
            setMetricType(e.target.value as GroupStatMetricNames)
          }
          className="!mt-1"
          options={availableOptions}
          required
          styles={{
            option: (base, state) => ({
              color: "#1C1D22",
              fontWeight: 500,
              borderTop: "1px solid #1C1D220A",
              ...baseSelectStyles.option?.(base, state)
            }),
            groupHeading: (base, state) => ({
              ...baseSelectStyles.groupHeading?.(base, state),
              background: "#FAF8F9",
              paddingTop: "8px",
              borderTop: "1px solid #1C1D220A",
              borderBottom: "0",
              margin: "0",
              color: "#1C1D22",
              fontSize: "13px"
            })
          }}
        />
        <BaseInput
          id="metric-title-input"
          type="text"
          label="Metric title"
          value={currentMetric.title || GROUP_STAT_LABEL[currentMetricType]}
          onChange={(v) => setCurrentMetricField("title", v.trim())}
          required
        />
        <RoleSelector
          selectedRoles={currentMetric.roles ?? []}
          setSelectedRoles={(roles) => setCurrentMetricField("roles", roles)}
        />
        <hr className="border-alpha-40 my-4" />
        <p className="font-medium text-content-700 text-sm">Preview</p>
        <MetricWidget isPreview metricData={currentMetric} />

        <Show if={INCOME_METRICS.includes(currentMetricType)}>
          <Checkbox
            label="Show badge"
            checked={currentMetric.badge}
            onChange={(e) => setCurrentMetricField("badge", e.target.checked)}
          />
        </Show>
        <Checkbox
          label="Show applicant type"
          checked={currentMetric.role_label}
          onChange={(e) =>
            setCurrentMetricField("role_label", e.target.checked)
          }
        />
      </div>

      <div className="mt-auto border-t border-alpha-100 flex gap-2 justify-center py-2">
        <Button
          variant={ButtonVariant.BASE}
          className="w-[154px]"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          className="w-[170px]"
          onClick={() =>
            saveMetric(currentMetric as ApplicationMetricData, onClose)
          }
          disabled={!allowSubmit}
          tooltip={allowSubmit ? "" : "Please select at least one role"}
          tooltipZIndex={101}
        >
          {Boolean(existingMetric) ? "Save changes" : "Add to dashboard"}
        </Button>
      </div>
    </div>
  );
};
